// 1:1 Port von club-member-registration/src/app/member-registration/models/registration-step.ts.
//
// Die acht gemeinsamen Konstanten kommen aus `@shared/member-application-steps` (sie stammen aus
// customer-apps und werden von der club-member-app mitbenutzt); die DREI hier definierten sind in
// Angular ebenfalls app-lokal.
//
// WICHTIG: Diese Objekte werden per IDENTITAET verglichen (`steps.indexOf(currentStep)`,
// `currentStep === steps[0]`, `steps.includes(...)`). Wer sie in ein `ref()` legt, bekommt einen
// reaktiven Proxy und jeder dieser Vergleiche schlaegt still fehl — `shallowRef` ist Pflicht
// (APP_MIGRATE.md §7 „ref() proxifiziert Objekt-Konstanten").
import {

    registrationStepAdditionalInformation,
    registrationStepDepartments,
    registrationStepDone,
    registrationStepFileUploads,
    registrationStepMemberBaseData,
    registrationStepMemberExtendedData,
    registrationStepPaymentDetails,
    registrationStepReview,
} from '@shared/member-application-steps';
import type { RegistrationStep } from '@shared/member-application-steps';
import type { RegistrationConfig } from './types/registration';

export type { RegistrationStep };
export {
    registrationStepAdditionalInformation,
    registrationStepDepartments,
    registrationStepDone,
    registrationStepFileUploads,
    registrationStepMemberBaseData,
    registrationStepMemberExtendedData,
    registrationStepPaymentDetails,
    registrationStepReview,
};

export const registrationStepStart: RegistrationStep = {
    path: 'start',
    title: 'Mitgliedsantrag',
    showNextButton: false,
    showBackButton: false,
    showInReview: false,
};

export const registrationStepDisabled: RegistrationStep = {
    path: 'disabled',
    title: 'Mitgliedsantrag',
    showNextButton: true,
    showBackButton: true,
    showInReview: false,
};

export const registrationStepDogData: RegistrationStep = {
    path: 'dogs',
    title: 'Hunde',
    showNextButton: true,
    showBackButton: true,
    showInReview: false,
};

/**
 * 1:1 Port von `MemberRegistrationService.getSteps()`.
 *
 * `askTeams` und `askClassifications` sind im Angular-Original **auskommentiert** — toter Code, die
 * zugehoerigen Steps existieren nicht und werden nicht nachgebaut (APP_MIGRATE.md §7).
 */
export function buildSteps(config: RegistrationConfig | null): RegistrationStep[] {
    if (!config?.active) {
        return [registrationStepDisabled];
    }

    const steps = [registrationStepStart, registrationStepMemberBaseData, registrationStepMemberExtendedData];

    if (config.askDogs) {
        steps.push(registrationStepDogData);
    }
    if (config.askDepartments) {
        steps.push(registrationStepDepartments);
    }
    if (config.allowDataUpload) {
        steps.push(registrationStepFileUploads);
    }

    steps.push(registrationStepPaymentDetails);

    if (
        config.askAdditionalInformation
        || config.askEmergencyContact
        || config.askFamilyMembers
        || (config.memberFilterFlags?.length ?? 0) > 0
        || (config.memberClassifications?.length ?? 0) > 0
    ) {
        steps.push(registrationStepAdditionalInformation);
    }

    steps.push(registrationStepReview);
    steps.push(registrationStepDone);

    return steps;
}

export function getNextStep(currentStep: RegistrationStep | null, steps: RegistrationStep[]): RegistrationStep | null {
    if (!currentStep) {
        return null;
    }
    return steps[steps.indexOf(currentStep) + 1] ?? null;
}

export function getPreviousStep(currentStep: RegistrationStep | null, steps: RegistrationStep[]): RegistrationStep | null {
    if (!currentStep) {
        return null;
    }
    return steps[steps.indexOf(currentStep) - 1] ?? null;
}

/** Alle Steps, die eine eigene Route haben — fuer die Zuordnung Pfad → Konstante in der Shell. */
export const allSteps: RegistrationStep[] = [
    registrationStepStart,
    registrationStepMemberBaseData,
    registrationStepMemberExtendedData,
    registrationStepDogData,
    registrationStepDepartments,
    registrationStepFileUploads,
    registrationStepPaymentDetails,
    registrationStepAdditionalInformation,
    registrationStepReview,
    registrationStepDone,
    registrationStepDisabled,
];
